import { z } from "zod";
import { clientInfoSchema, serverInfoSchema } from "./handshake.ts";
import { aiEvents, aiModelsRequest, changeSetAgentEvents, commandAgentEvents } from "./ai.ts";
import { event, notification, request } from "./defs.ts";
import { lspEvents, lspNotifications, lspRequests } from "./lsp.ts";
import { environmentRequests } from "./environment.ts";
import { desktopUpdateServerEvents, desktopUpdateServerRequests } from "./desktopUpdate.ts";
import { gitReadinessSchema, gitRequests } from "./git.ts";
import { projectSearchRequest } from "./projectSearch.ts";
import { encryptedEnvelopeSchema, providerTypeSchema } from "./provisioning.ts";
import {
  transferStartRequest,
  transferCompleteRequest,
  transferCancelRequest,
  transferProgressEvent,
  importAttachmentRequest,
  deleteAttachmentRequest,
} from "./transfer.ts";
import {
  dirEntrySchema,
  fileContentSchema,
  inventorySchema,
  machineSummarySchema,
  relativePathSchema,
  terminalIdSchema,
  terminalInfoSchema,
  terminalInputSchema,
  terminalSizeSchema,
  projectIdSchema,
  projectNameSchema,
  projectSchema,
  workspaceIdSchema,
  workspaceSchema,
} from "./schemas.ts";

/**
 * Message registry. Every (channel, type) allowed on the wire is declared here exactly once;
 * anything not declared is rejected by server and agent. Keys are "<channel>/<type>".
 */
const empty = z.strictObject({});

/** Requests executed by the agent (routed by the server after an ownership check). */
export const agentRequests = {
  /** Models of every provider configured on the machine (read-only; no credentials). */
  "agent/ai.models": aiModelsRequest,
  /** Secure credential provisioning (browser ciphertext envelope relayed to agent). */
  "agent/credential.provision": request(
    "agent",
    "credential.provision",
    z.strictObject({
      envelope: encryptedEnvelopeSchema,
      credentialId: z.string().min(1).max(64),
      provider: providerTypeSchema,
      label: z.string().min(1).max(100),
      baseUrl: z.string().url().optional(),
    }),
    z.strictObject({ ok: z.boolean(), credentialId: z.string() }),
  ),
  /** Test connection for a credential locally on the agent using local secret store. */
  "agent/credential.test": request(
    "agent",
    "credential.test",
    z.strictObject({
      credentialId: z.string().min(1).max(64),
      provider: providerTypeSchema,
      baseUrl: z.string().url().optional(),
    }),
    z.strictObject({
      ok: z.boolean(),
      modelCount: z.number().int().nonnegative().optional(),
      error: z.string().optional(),
    }),
  ),
  /** Remove a credential secret from local storage. */
  "agent/credential.remove": request(
    "agent",
    "credential.remove",
    z.strictObject({ credentialId: z.string().min(1).max(64) }),
    z.strictObject({ ok: z.boolean() }),
  ),
  /** List local non-secret credential availability metadata. */
  "agent/credentials.list": request(
    "agent",
    "credentials.list",
    empty,
    z.strictObject({
      credentials: z.array(
        z.strictObject({
          id: z.string(),
          configured: z.boolean(),
        }),
      ),
    }),
  ),
  /** Projects registered on the machine (each belongs to exactly one Workspace). */
  "system/projects.list": request(
    "system",
    "projects.list",
    empty,
    z.strictObject({ projects: z.array(projectSchema) }),
  ),
  /** Approved Workspaces of the machine (approved locally with `racler workspace add`; never by the browser). */
  "system/workspaces.list": request(
    "system",
    "workspaces.list",
    empty,
    z.strictObject({ workspaces: z.array(workspaceSchema) }),
  ),
  /** Sub-folders of a folder INSIDE an approved Workspace (directories only; the agent enforces containment). */
  "system/projects.browse": request(
    "system",
    "projects.browse",
    z.strictObject({ workspaceId: workspaceIdSchema, path: relativePathSchema }),
    z.strictObject({
      path: z.string(),
      folders: z.array(z.string().max(255)).max(1000),
      truncated: z.boolean(),
    }),
  ),
  /** Register an existing folder inside an approved Workspace as a Project (never the Workspace itself). */
  "system/projects.add": request(
    "system",
    "projects.add",
    z.strictObject({
      workspaceId: workspaceIdSchema,
      path: relativePathSchema.min(1),
      name: projectNameSchema.optional(),
    }),
    /** `git`: Git readiness of the registered Project (read-only check; adding never initializes Git). */
    z.strictObject({ project: projectSchema, git: gitReadinessSchema.optional() }),
  ),
  /** Create a new folder `name` inside `path` (inside an approved Workspace) and register it as a Project. */
  "system/projects.create": request(
    "system",
    "projects.create",
    z.strictObject({
      workspaceId: workspaceIdSchema,
      path: relativePathSchema,
      name: z.string().min(1).max(255),
    }),
    /** `git`: readiness AFTER racler prepared the brand-new Project (when Git is available and safe). */
    z.strictObject({ project: projectSchema, git: gitReadinessSchema.optional() }),
  ),
  /** Clone a Git repository `url` into folder `name` inside `path` (inside an approved Workspace) and register it as a Project. */
  "system/projects.clone": request(
    "system",
    "projects.clone",
    z.strictObject({
      workspaceId: workspaceIdSchema,
      path: relativePathSchema,
      name: z.string().min(1).max(255),
      url: z.string().min(1).max(2048),
    }),
    z.strictObject({ project: projectSchema, git: gitReadinessSchema.optional() }),
  ),

  /** Removes the REGISTRATION only. Files are never deleted. */
  "system/projects.remove": request(
    "system",
    "projects.remove",
    z.strictObject({ projectId: projectIdSchema }),
    empty,
  ),
  "filesystem/listDirectory": request(
    "filesystem",
    "listDirectory",
    z.strictObject({ projectId: projectIdSchema, path: relativePathSchema }),
    z.strictObject({ path: z.string(), entries: z.array(dirEntrySchema), truncated: z.boolean() }),
  ),
  "filesystem/searchEntries": request(
    "filesystem",
    "searchEntries",
    z.strictObject({
      projectId: projectIdSchema,
      query: z.string().max(200).default(""),
      limit: z.number().int().min(1).max(200).default(50),
    }),
    z.strictObject({
      entries: z.array(
        z.strictObject({
          path: z.string(),
          kind: z.enum(["file", "directory"]),
        }),
      ),
    }),
  ),
  "filesystem/searchProject": projectSearchRequest,
  "filesystem/readFile": request(
    "filesystem",
    "readFile",
    z.strictObject({ projectId: projectIdSchema, path: relativePathSchema }),
    z.strictObject({ path: z.string(), content: z.string(), size: z.number(), mtimeMs: z.number() }),
  ),
  "filesystem/writeFile": request(
    "filesystem",
    "writeFile",
    z.strictObject({
      projectId: projectIdSchema,
      path: relativePathSchema,
      content: fileContentSchema,
      /** If set, fails with CONFLICT when the file changed on disk since it was read. */
      expectedMtimeMs: z.number().optional(),
    }),
    z.strictObject({ path: z.string(), size: z.number(), mtimeMs: z.number() }),
  ),
  /** Create a new file (fails with ALREADY_EXISTS). Missing parent directories are created. */
  "filesystem/createFile": request(
    "filesystem",
    "createFile",
    z.strictObject({
      projectId: projectIdSchema,
      path: relativePathSchema,
      content: fileContentSchema.optional(),
    }),
    z.strictObject({ path: z.string(), size: z.number(), mtimeMs: z.number() }),
  ),
  "filesystem/createDirectory": request(
    "filesystem",
    "createDirectory",
    z.strictObject({ projectId: projectIdSchema, path: relativePathSchema }),
    z.strictObject({ path: z.string() }),
  ),
  /** Rename/move an entry inside the Project. Never overwrites (ALREADY_EXISTS). Acts on symlinks themselves. */
  "filesystem/rename": request(
    "filesystem",
    "rename",
    z.strictObject({ projectId: projectIdSchema, from: relativePathSchema, to: relativePathSchema }),
    z.strictObject({ from: z.string(), to: z.string() }),
  ),
  /** Delete a file, symlink (the link only) or directory (`recursive` required if not empty). */
  "filesystem/delete": request(
    "filesystem",
    "delete",
    z.strictObject({ projectId: projectIdSchema, path: relativePathSchema, recursive: z.boolean() }),
    z.strictObject({ path: z.string() }),
  ),
  "terminal/terminal.create": request(
    "terminal",
    "terminal.create",
    z.strictObject({ projectId: projectIdSchema, ...terminalSizeSchema }),
    z.strictObject({ terminal: terminalInfoSchema }),
  ),
  "terminal/terminal.list": request(
    "terminal",
    "terminal.list",
    empty,
    z.strictObject({ terminals: z.array(terminalInfoSchema) }),
  ),
  "terminal/terminal.attach": request(
    "terminal",
    "terminal.attach",
    z.strictObject({ terminalId: terminalIdSchema }),
    z.strictObject({ terminal: terminalInfoSchema, scrollback: z.string() }),
  ),
  "terminal/terminal.kill": request(
    "terminal",
    "terminal.kill",
    z.strictObject({ terminalId: terminalIdSchema }),
    empty,
  ),
  "transfer/transfer.start": transferStartRequest,
  "transfer/transfer.complete": transferCompleteRequest,
  "transfer/transfer.cancel": transferCancelRequest,
  "transfer/attachment.import": importAttachmentRequest,
  /** Format document buffer using Project formatter (Prettier or Biome). Never writes to disk. */
  "editor/formatDocument": request(
    "editor",
    "formatDocument",
    z.strictObject({
      projectId: projectIdSchema,
      path: relativePathSchema,
      content: fileContentSchema,
    }),
    z.strictObject({
      content: z.string(),
      formatter: z.enum(["prettier", "biome"]),
      changed: z.boolean(),
    }),
  ),
  "transfer/attachment.delete": deleteAttachmentRequest,
  ...lspRequests,
  ...environmentRequests,
  ...gitRequests,
} as const;


export const agentNotifications = {
  ...lspNotifications,
  "terminal/terminal.input": notification(
    "terminal",
    "terminal.input",
    z.strictObject({ terminalId: terminalIdSchema, data: terminalInputSchema }),
  ),
  "terminal/terminal.resize": notification(
    "terminal",
    "terminal.resize",
    z.strictObject({ terminalId: terminalIdSchema, ...terminalSizeSchema }),
  ),
} as const;

export const agentEvents = {
  "terminal/terminal.output": event(
    "terminal",
    "terminal.output",
    z.strictObject({ terminalId: terminalIdSchema, data: z.string() }),
  ),
  "terminal/terminal.exit": event(
    "terminal",
    "terminal.exit",
    z.strictObject({ terminalId: terminalIdSchema, exitCode: z.number().int().nullable() }),
  ),
  "transfer/transfer.progress": transferProgressEvent,
  ...aiEvents,
  ...changeSetAgentEvents,
  ...commandAgentEvents,
  ...lspEvents,
} as const;

/**
 * Agent -> SERVER ONLY (deliberately not part of `agentEvents`, so it is never forwarded to browsers). The agent
 * reports the ids, display names and Workspace-relative paths of its approved Workspaces and registered Projects so
 * the server can keep a metadata index (authorization context, audit, future aggregation by Workspace / Project /
 * Profile). Absolute filesystem paths are never part of it: the agent stays authoritative for them. The machine is
 * taken from the authenticated connection, never from the payload.
 */
export const agentInventoryEvent = event("system", "inventory", inventorySchema);

/** Server-local requests on the browser hop (envelope has no machineId). */
export const serverRequests = {
  "system/machines.list": request(
    "system",
    "machines.list",
    empty,
    z.strictObject({ machines: z.array(machineSummarySchema) }),
  ),
  /** Get the authenticated provisioning public key for a machine. */
  "system/machine.provisioningKey": request(
    "system",
    "machine.provisioningKey",
    z.strictObject({ machineId: z.uuid() }),
    z.strictObject({
      machineId: z.uuid(),
      publicKeySpki: z.string().min(1),
      online: z.boolean(),
    }),
  ),
  /** Start receiving events (terminal output) for a machine. Ownership-checked. */
  "system/machine.subscribe": request(
    "system",
    "machine.subscribe",
    z.strictObject({ machineId: z.uuid() }),
    z.strictObject({ online: z.boolean() }),
  ),
  "system/machine.unsubscribe": request(
    "system",
    "machine.unsubscribe",
    z.strictObject({ machineId: z.uuid() }),
    empty,
  ),
  /** Remote Desktop updates (V1): update overview, channel, confirmation snapshot, start. */
  ...desktopUpdateServerRequests,
} as const;

export const serverEvents = {
  "system/machine.status": event(
    "system",
    "machine.status",
    z.strictObject({ machineId: z.uuid(), online: z.boolean() }),
  ),
  ...desktopUpdateServerEvents,
} as const;

/** Agent ↔ server handshake (agent hop only). */
export const AGENT_AUTH_CONTEXT = "racler-agent-auth:v1";

export const agentHandshake = {
  challenge: event(
    "system",
    "auth.challenge",
    z.looseObject({
      nonce: z.string().min(32).max(128),
      serverOrigin: z.string().max(512),
      protocolVersion: z.number().int().positive().optional(),
    }),
  ),
  response: event(
    "system",
    "auth.response",
    z.looseObject({
      machineId: z.uuid(),
      /** base64url Ed25519 signature over agentAuthMessage(...) */
      signature: z.string().min(1).max(256),
      agentVersion: z.string().max(64),
      platform: z.string().max(64),
      /** Dedicated RSA-OAEP provisioning public key (SPKI base64). */
      provisioningPublicKey: z.string().min(1).max(4096).optional(),
      /** Structured client application, platform, arch, and capability report */
      client: clientInfoSchema.optional(),
    }),
  ),
  ok: event(
    "system",
    "auth.ok",
    z.looseObject({
      machineId: z.uuid(),
      name: z.string(),
      /** M3-WP7 (optional, backwards compatible): the owning account, so the app can label a Pairing. */
      account: z.strictObject({ displayName: z.string().max(200) }).optional(),
      /** Server compatibility & capabilities report */
      server: serverInfoSchema.optional(),
    }),
  ),
} as const;

/**
 * WebSocket close codes on /ws/agent that the agent must interpret (M3-WP1).
 *  - `AUTH_REJECTED` is TERMINAL: the machine is unknown, revoked, or signed with the wrong key. Retrying cannot help.
 *  - `REPLACED` is TERMINAL for the old process: the same machine connected elsewhere. Reconnecting would just replace
 *    the newer connection and start an endless tug of war.
 *  - `UPGRADE_REQUIRED` is TERMINAL: client is below the server's minimum supported version; client update required.
 *  - everything else (including `AUTH_TIMEOUT`, 1011 and abnormal closes such as 1006) is transient: reconnect.
 */
export const AGENT_CLOSE = {
  REPLACED: 4000,
  AUTH_REJECTED: 4003,
  UPGRADE_REQUIRED: 4004,
  AUTH_TIMEOUT: 4008,
  INTERNAL_ERROR: 1011,
} as const;

/** The exact string the agent signs: binds server origin, machine id and the fresh nonce. */
export function agentAuthMessage(serverOrigin: string, machineId: string, nonce: string): string {
  return `${AGENT_AUTH_CONTEXT}|${serverOrigin}|${machineId}|${nonce}`;
}

/**
 * Self-revocation (M3-WP7). A DEDICATED context: a signature made for the agent handshake can never be replayed as a
 * revocation and vice versa. `issuedAt` is an integer count of milliseconds since the Unix epoch, written in plain
 * decimal (no padding, no exponent, no sign); the same builder is used by the signer (runtime) and the verifier (server).
 * A revocation statement never expires, because it may be delivered days later from an offline queue.
 */
export const AGENT_REVOKE_CONTEXT = "racler-agent-revoke-v1";

/** Largest `issuedAt` accepted anywhere (keeps the decimal form unambiguous and inside the safe-integer range). */
export const MAX_REVOKE_ISSUED_AT = 8_640_000_000_000_000;

export function agentRevokeMessage(serverOrigin: string, machineId: string, issuedAt: number): string {
  if (!Number.isSafeInteger(issuedAt) || issuedAt <= 0 || issuedAt > MAX_REVOKE_ISSUED_AT) {
    throw new RangeError("issuedAt must be a positive integer number of epoch milliseconds");
  }
  return `${AGENT_REVOKE_CONTEXT}|${serverOrigin}|${machineId}|${issuedAt.toString(10)}`;
}
